// Display pages count promotional slots; Shopify source pages always keep their original fixed size.
// Cumulative product offsets make every product appear exactly once, even when promo counts vary by page.
export function buildCollectionPages(productCount, pageSize, columns, promos) {
  const pages = [];
  const target = Math.max(columns, Math.floor(pageSize / columns) * columns);
  let offset = 0;
  while (offset < productCount) {
    const number = pages.length + 1;
    const remaining = productCount - offset;
    const active = promos.filter(promo => promo.position > 0 && promo.position <= Math.min(pageSize, remaining)
      && (!promo.pages.length || promo.pages.includes(number)));
    const promoSlots = active.reduce((sum, promo) => sum + promo.span, 0);
    // Preserve late insertion positions. If necessary, add a row instead of losing a configured promo.
    const lastPosition = Math.max(1, ...active.map(promo => promo.position));
    let count = Math.max(lastPosition, target - promoSlots);
    count += (columns - (count + promoSlots) % columns) % columns;
    count = Math.min(count, remaining);
    pages.push({ number, offset, count, promos: active });
    offset += count;
  }
  return pages;
}

if (typeof window !== 'undefined' && !customElements.get('balanced-collection-grid')) {
  class BalancedCollectionGrid extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.grid = this.querySelector('product-list');
      this.nav = this.querySelector('[data-balanced-pagination]');
      this.nativeNav = this.querySelector('[data-native-pagination]');
      this.templates = [...this.querySelectorAll('template[data-grid-promo]')];
      if (!this.grid || !this.hasAttribute('enabled')) return;

      this.sourceSize = Number(this.dataset.pageSize);
      this.total = Number(this.dataset.productCount);
      this.url = new URL(location.href);
      this.cache = new Map([[Number(this.dataset.sourcePage), this.readCards(this)]]);
      this.revision = 0;
      this.refreshPlan();
      const signal = this.controller.signal;
      this.nav.addEventListener('click', event => {
        const link = event.target.closest('a[data-grid-page]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.showPage(Number(link.dataset.gridPage), { history: true, scroll: true });
      }, { signal });
      // Faceting and sorting must start at the first display page; preserve Shopify's existing facet handler.
      this.addEventListener('facet:update', event => {
        if (this.nativeNav.contains(event.target)) return;
        event.detail.url.searchParams.delete('grid_page');
        event.detail.url.searchParams.delete('page');
      }, { signal });
      window.addEventListener('popstate', () => {
        const next = new URL(location.href);
        if (this.filterKey(next) !== this.filterKey(this.url)) {
          location.reload();
          return;
        }
        this.url = next;
        this.showPage(this.requestedPage());
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => {
        const previous = this.signature;
        const offset = this.pages[this.currentPage - 1]?.offset || 0;
        this.refreshPlan();
        if (previous !== this.signature) {
          const page = this.pages.findIndex(item => offset < item.offset + item.count) + 1;
          this.showPage(page || 1, { replace: true });
        }
      });
      this.resizeObserver.observe(this.grid);
      this.showPage(this.requestedPage());
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.revision++;
    }

    requestedPage() {
      return Number(this.url.searchParams.get('grid_page') || this.url.searchParams.get('page')) || 1;
    }

    filterKey(url) {
      const key = new URL(url);
      ['page', 'grid_page', 'section_id'].forEach(name => key.searchParams.delete(name));
      key.searchParams.sort();
      return key.pathname + key.search;
    }

    readCards(root) {
      return [...root.querySelectorAll('product-list > product-card')].map(card => card.cloneNode(true));
    }

    refreshPlan() {
      const columns = Math.max(1, Number(getComputedStyle(this.grid).getPropertyValue('--product-list-items-per-row')) || 4);
      const promos = this.templates.map((template, index) => {
        let span = 1;
        if (template.dataset.kind === 'section') {
          const probe = template.content.firstElementChild.cloneNode(false);
          probe.style.visibility = 'hidden';
          this.grid.append(probe);
          span = Math.min(columns, parseInt(getComputedStyle(probe).gridColumnEnd.replace('span', '').trim()) || 1);
          probe.remove();
        }
        return { index, position: Number(template.dataset.position), pages: JSON.parse(template.dataset.pages || '[]') || [], span };
      });
      this.signature = JSON.stringify([columns, promos]);
      this.pages = buildCollectionPages(this.total, this.sourceSize, columns, promos);
    }

    pageUrl(number) {
      const url = new URL(this.url);
      url.searchParams.delete('section_id');
      // A separate parameter avoids requesting a nonexistent Shopify source page when promos add display pages.
      url.searchParams.delete('page');
      url.searchParams.set('grid_page', number);
      return url;
    }

    async sourcePage(number) {
      if (this.cache.has(number)) return this.cache.get(number);
      const url = new URL(this.url);
      url.searchParams.delete('grid_page');
      url.searchParams.set('page', number);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Could not load collection products');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = this.readCards(html);
      const expected = Math.min(this.sourceSize, this.total - (number - 1) * this.sourceSize);
      if (cards.length !== expected) throw new Error('Incomplete collection response');
      this.cache.set(number, cards);
      return cards;
    }

    async showPage(number, options = {}) {
      if (!this.pages.length) return;
      number = Math.max(1, Math.min(this.pages.length, Math.floor(number) || 1));
      const page = this.pages[number - 1];
      const revision = ++this.revision;
      this.grid.setAttribute('aria-busy', 'true');
      try {
        const first = Math.floor(page.offset / this.sourceSize) + 1;
        const last = Math.floor((page.offset + page.count - 1) / this.sourceSize) + 1;
        const chunks = await Promise.all(Array.from({ length: last - first + 1 }, (_, i) => this.sourcePage(first + i)));
        if (!this.isConnected || revision !== this.revision) return;
        const start = page.offset - (first - 1) * this.sourceSize;
        const cards = chunks.flat().slice(start, start + page.count);
        if (cards.length !== page.count) throw new Error('Incomplete display page');
        const fragment = document.createDocumentFragment();
        cards.forEach((card, index) => {
          page.promos.filter(promo => promo.position === index + 1).forEach(promo => {
            fragment.append(this.templates[promo.index].content.cloneNode(true));
          });
          const clone = card.cloneNode(true);
          // Source cards may have already been animated. Insert the complete display page visibly.
          clone.style.removeProperty('opacity');
          clone.style.removeProperty('transform');
          fragment.append(clone);
        });
        this.grid.replaceChildren(fragment);
        this.currentPage = number;
        this.nativeNav.hidden = true;
        this.nav.hidden = this.pages.length < 2;
        this.nav.querySelector('[data-grid-current]').textContent = `${number} / ${this.pages.length}`;
        [-1, 1].forEach(direction => {
          const link = this.nav.querySelector(`[data-grid-direction="${direction}"]`);
          const target = number + direction;
          const disabled = target < 1 || target > this.pages.length;
          link.hidden = disabled;
          link.nextElementSibling.hidden = !disabled;
          link.dataset.gridPage = target;
          link.href = this.pageUrl(target);
        });
        this.querySelector('[data-grid-error]').hidden = true;
        if (options.history || options.replace) {
          this.url = this.pageUrl(number);
          window.history[options.history ? 'pushState' : 'replaceState']({}, '', this.url);
        }
        if (options.scroll) {
          this.grid.scrollIntoView({ block: 'start' });
          this.grid.setAttribute('tabindex', '-1');
          this.grid.focus({ preventScroll: true });
        }
      } catch (error) {
        if (error.name !== 'AbortError' && revision === this.revision) {
          // Keep the last complete page in place, with a retry link; never display an incomplete product slice.
          const message = this.querySelector('[data-grid-error]');
          message.hidden = false;
          message.querySelector('a').href = this.pageUrl(number);
          console.warn('Collection pagination:', error.message);
        }
      } finally {
        if (revision === this.revision) this.grid.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('balanced-collection-grid', BalancedCollectionGrid);
}
